import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE, buildCanonical, type SeoMeta, type HreflangMap } from './config';

const SUPPORTED_LOCALES = ['en', 'hi', 'ta', 'te', 'bn'] as const;

function inferHreflang(pathname: string): HreflangMap {
  const base = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const map: HreflangMap = {};
  for (const loc of SUPPORTED_LOCALES) {
    const prefix = loc === 'en' ? '' : `/${loc}`;
    map[loc] = `${SITE.url}${prefix}${base === '/' ? '' : base}`;
  }
  map['x-default'] = `${SITE.url}${base === '/' ? '' : base}`;
  return map;
}

interface SeoHeadProps extends SeoMeta {
  schemas?: Record<string, unknown>[];
}

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name') {
  const selector = `meta[${attr}="${name}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function clearHreflang() {
  document.head
    .querySelectorAll<HTMLLinkElement>('link[rel="alternate"][hreflang]')
    .forEach((el) => el.remove());
}

function clearJsonLd() {
  document.head
    .querySelectorAll<HTMLScriptElement>('script[data-parksy-jsonld]')
    .forEach((el) => el.remove());
}

export default function SeoHead({ schemas = [], ...meta }: SeoHeadProps) {
  const location = useLocation();

  const canonical = useMemo(
    () => meta.canonical ?? buildCanonical(location.pathname),
    [meta.canonical, location.pathname]
  );
  const ogImage = meta.ogImage ?? SITE.ogImage;
  const description = meta.description ?? SITE.description;
  const hreflang = meta.hreflang ?? inferHreflang(location.pathname);

  useEffect(() => {
    document.title = meta.title;

    if (meta.noindex) {
      setMeta('robots', 'noindex,nofollow');
    } else {
      setMeta('robots', 'index,follow,max-image-preview:large');
    }

    setMeta('description', description);
    setMeta('keywords', 'parking, India, find parking, list parking, monthly parking, two-wheeler parking, EV parking, crowdsourced');
    setMeta('author', 'The Parksy team');
    setMeta('viewport', 'width=device-width, initial-scale=1.0, viewport-fit=cover');
    setMeta('theme-color', '#16a34a');

    setLink('canonical', canonical);
    setMeta('og:title', meta.title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:url', canonical, 'property');
    setMeta('og:image', ogImage, 'property');
    setMeta('og:type', meta.ogType ?? 'website', 'property');
    setMeta('og:site_name', SITE.name, 'property');
    setMeta('og:locale', SITE.locale, 'property');

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', meta.title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', ogImage);
    setMeta('twitter:site', SITE.twitterHandle);

    if (meta.publishedTime) setMeta('article:published_time', meta.publishedTime, 'property');
    if (meta.modifiedTime) setMeta('article:modified_time', meta.modifiedTime, 'property');

    clearHreflang();
    for (const [lang, href] of Object.entries(hreflang)) {
      setLink('alternate', href, lang);
    }

    clearJsonLd();
    for (const schema of schemas) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.parksyJsonld = 'true';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    return () => {
      clearJsonLd();
    };
  }, [meta.title, description, canonical, ogImage, meta.ogType, meta.noindex, hreflang, meta.publishedTime, meta.modifiedTime, schemas]);

  return null;
}
